import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { clearSiteSettingsCache } from "@/hooks/useSiteSettings";
import ReactMarkdown from "react-markdown";
import { Save, Eye, EyeOff, Trash2, FileText, BookOpen, Settings as SettingsIcon, Upload as UploadIcon } from "lucide-react";

const TABS = [
  { id: "legal", label: "Legal pages", icon: FileText },
  { id: "blog", label: "Blog", icon: BookOpen },
  { id: "settings", label: "Site settings", icon: SettingsIcon },
];

export default function AdminContent() {
  const [tab, setTab] = useState("legal");
  return (
    <>
      <PageHeader title="Content & Site" subtitle="Admin-managed legal pages, blog, and footer/site settings." />
      <div className="card" style={{ padding: 0 }}>
        <div style={{ display: "flex", borderBottom: "1px solid var(--line)" }}>
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.id;
            return (
              <button key={t.id} data-testid={`content-tab-${t.id}`}
                      onClick={() => setTab(t.id)}
                      style={{ display: "flex", alignItems: "center", gap: 8, padding: "14px 20px",
                               background: "transparent", border: "none", borderBottom: active ? "2px solid var(--amber-500)" : "2px solid transparent",
                               color: active ? "var(--ink)" : "var(--ink-mute)", cursor: "pointer",
                               fontSize: 13, fontWeight: 500 }}>
                <Icon size={14} /> {t.label}
              </button>
            );
          })}
        </div>
        <div style={{ padding: 24 }}>
          {tab === "legal" && <LegalEditor />}
          {tab === "blog" && <BlogEditor />}
          {tab === "settings" && <SiteSettingsEditor />}
        </div>
      </div>
    </>
  );
}

// ============================================================
// Legal pages
// ============================================================
function LegalEditor() {
  const [pages, setPages] = useState([]);
  const [activeSlug, setActiveSlug] = useState("terms");
  const [doc, setDoc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [preview, setPreview] = useState(false);
  const [draft, setDraft] = useState({ title: "", body_markdown: "", published: false });

  const reload = () => api.get("/admin/content/legal").then(r => setPages(r.data));
  useEffect(() => { reload(); }, []);

  useEffect(() => {
    if (!activeSlug) return;
    api.get(`/admin/content/legal/${activeSlug}`).then(r => {
      setDoc(r.data);
      setDraft({ title: r.data.title || "", body_markdown: r.data.body_markdown || "", published: !!r.data.published });
    });
  }, [activeSlug]);

  const save = async () => {
    setSaving(true); setErr("");
    try {
      const r = await api.put(`/admin/content/legal/${activeSlug}`, draft);
      setDoc(r.data);
      setDraft({ title: r.data.title, body_markdown: r.data.body_markdown, published: !!r.data.published });
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "240px 1fr", gap: 24 }}>
      <div>
        <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".1em", color: "var(--ink-mute)", marginBottom: 8 }}>SLUGS</div>
        {pages.map(p => (
          <button key={p.slug}
                  data-testid={`legal-slug-${p.slug}`}
                  onClick={() => setActiveSlug(p.slug)}
                  style={{ width: "100%", textAlign: "left", padding: "10px 12px",
                           background: activeSlug === p.slug ? "rgba(245,158,11,.08)" : "transparent",
                           border: "1px solid", borderColor: activeSlug === p.slug ? "var(--amber-500)" : "var(--line)",
                           borderRadius: 4, marginBottom: 6, cursor: "pointer", color: "var(--ink)",
                           display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 13 }}>
            <span>{p.slug}</span>
            <span className="font-mono" style={{ fontSize: 10, color: p.published ? "var(--emerald)" : "var(--ink-mute)" }}>
              {p.published ? "LIVE" : "DRAFT"}
            </span>
          </button>
        ))}
      </div>

      {doc && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
            <div>
              <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>
                /{activeSlug} · v{doc.version}
              </div>
              <div className="font-mono" style={{ fontSize: 10, color: "var(--ink-mute)" }}>
                last edited {doc.updated_at ? new Date(doc.updated_at).toLocaleString() : "—"}
              </div>
            </div>
            <button data-testid="legal-toggle-preview" className="btn btn-ghost"
                    onClick={() => setPreview(p => !p)} style={{ fontSize: 12 }}>
              {preview ? <><EyeOff size={13} /> Edit</> : <><Eye size={13} /> Preview</>}
            </button>
          </div>

          {!preview && (
            <>
              <label className="label">Title</label>
              <input className="input" data-testid="legal-title-input" value={draft.title}
                     onChange={e => setDraft({ ...draft, title: e.target.value })}
                     style={{ marginBottom: 12 }} />

              <label className="label">Body (Markdown)</label>
              <textarea className="input" data-testid="legal-body-input"
                        value={draft.body_markdown}
                        onChange={e => setDraft({ ...draft, body_markdown: e.target.value })}
                        style={{ minHeight: 380, fontFamily: "ui-monospace, monospace", fontSize: 13 }} />

              <label style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12, fontSize: 13 }}>
                <input type="checkbox" data-testid="legal-published-toggle" checked={draft.published}
                       onChange={e => setDraft({ ...draft, published: e.target.checked })} />
                Published (visible to public at <code style={{ background: "rgba(255,255,255,.04)", padding: "2px 6px" }}>/{activeSlug}</code>)
              </label>
            </>
          )}

          {preview && (
            <article className="legal-body" style={{ padding: 24, background: "rgba(255,255,255,.02)", borderRadius: 4 }}>
              <h2 className="font-serif-display" style={{ marginTop: 0, fontSize: 32 }}>{draft.title}</h2>
              <ReactMarkdown>{draft.body_markdown}</ReactMarkdown>
            </article>
          )}

          {err && <div style={{ color: "var(--red)", marginTop: 10 }} data-testid="legal-error">{err}</div>}
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <button className="btn btn-primary" data-testid="legal-save" onClick={save} disabled={saving}>
              <Save size={13} /> {saving ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================
// Blog
// ============================================================
function BlogEditor() {
  const [posts, setPosts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [err, setErr] = useState("");
  const [uploading, setUploading] = useState(false);

  const reload = () => api.get("/admin/content/blog").then(r => setPosts(r.data));
  useEffect(() => { reload(); }, []);

  const blank = { slug: "", title: "", excerpt: "", body_markdown: "", cover_image_url: "", author_name: "CanadaMRO Exchange", status: "draft" };

  const save = async () => {
    setErr("");
    try {
      const payload = { ...editing, cover_image_url: editing.cover_image_url || null };
      if (editing.id) await api.put(`/admin/content/blog/${editing.id}`, payload);
      else await api.post("/admin/content/blog", payload);
      setEditing(null);
      reload();
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/admin/content/blog/${id}`);
    reload();
  };

  const uploadCover = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("purpose", "blog_cover");
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = r.data.url || r.data.public_url || r.data.path || `/api/uploads/${r.data.id}/file`;
      setEditing(e => ({ ...e, cover_image_url: url }));
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setUploading(false); }
  };

  if (editing) {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
          <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>
            {editing.id ? "EDIT POST" : "NEW POST"}
          </div>
          <button className="btn btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div><label className="label">Slug *</label>
            <input className="input" data-testid="blog-slug" value={editing.slug}
                   onChange={e => setEditing({ ...editing, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-") })} />
          </div>
          <div><label className="label">Status</label>
            <select className="input" data-testid="blog-status" value={editing.status}
                    onChange={e => setEditing({ ...editing, status: e.target.value })}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
            </select></div>
        </div>
        <label className="label" style={{ marginTop: 10 }}>Title *</label>
        <input className="input" data-testid="blog-title" value={editing.title}
               onChange={e => setEditing({ ...editing, title: e.target.value })} />
        <label className="label" style={{ marginTop: 10 }}>Excerpt</label>
        <textarea className="input" data-testid="blog-excerpt" value={editing.excerpt}
                  onChange={e => setEditing({ ...editing, excerpt: e.target.value })}
                  style={{ minHeight: 60 }} />
        <label className="label" style={{ marginTop: 10 }}>Cover image</label>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          {editing.cover_image_url && <img src={editing.cover_image_url} alt="" style={{ width: 120, height: 70, objectFit: "cover", borderRadius: 4 }} />}
          <label className="btn btn-ghost" style={{ cursor: "pointer", fontSize: 12 }}>
            <UploadIcon size={13} /> {uploading ? "Uploading…" : "Upload cover"}
            <input type="file" accept="image/*" data-testid="blog-cover-upload" hidden
                   onChange={e => e.target.files?.[0] && uploadCover(e.target.files[0])} />
          </label>
          {editing.cover_image_url && (
            <button className="btn btn-ghost" style={{ fontSize: 12 }} onClick={() => setEditing({ ...editing, cover_image_url: "" })}>Clear</button>
          )}
        </div>
        <label className="label" style={{ marginTop: 10 }}>Body (Markdown)</label>
        <textarea className="input" data-testid="blog-body" value={editing.body_markdown}
                  onChange={e => setEditing({ ...editing, body_markdown: e.target.value })}
                  style={{ minHeight: 320, fontFamily: "ui-monospace, monospace", fontSize: 13 }} />
        {err && <div style={{ color: "var(--red)", marginTop: 10 }} data-testid="blog-error">{err}</div>}
        <div style={{ marginTop: 14 }}>
          <button className="btn btn-primary" data-testid="blog-save" onClick={save}><Save size={13} /> Save</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
        <div className="font-mono" style={{ fontSize: 11, letterSpacing: ".1em", color: "var(--ink-mute)" }}>
          {posts.length} POST(S)
        </div>
        <button className="btn btn-primary" data-testid="blog-new" onClick={() => setEditing(blank)}>+ New post</button>
      </div>
      {posts.length === 0 ? (
        <div style={{ color: "var(--ink-mute)", textAlign: "center", padding: 32 }}>No posts yet.</div>
      ) : (
        <table className="data">
          <thead><tr><th>Title</th><th>Slug</th><th>Status</th><th>Updated</th><th></th></tr></thead>
          <tbody>
            {posts.map(p => (
              <tr key={p.id} data-testid={`blog-row-${p.slug}`}>
                <td>{p.title}</td>
                <td className="font-mono" style={{ color: "var(--amber-400)", fontSize: 12 }}>/{p.slug}</td>
                <td className="font-mono" style={{ fontSize: 11, color: p.status === "published" ? "var(--emerald)" : "var(--ink-mute)" }}>
                  {p.status.toUpperCase()}
                </td>
                <td className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)" }}>
                  {new Date(p.updated_at).toLocaleDateString()}
                </td>
                <td style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <button className="btn btn-ghost" data-testid={`blog-edit-${p.slug}`} onClick={() => setEditing(p)} style={{ padding: "5px 10px" }}>Edit</button>
                  <button className="btn btn-danger" data-testid={`blog-delete-${p.slug}`} onClick={() => del(p.id)} style={{ padding: "5px 10px" }}><Trash2 size={13} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ============================================================
// Site settings
// ============================================================
function SiteSettingsEditor() {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/content/site-settings").then(r => setS(r.data));
  }, []);

  const save = async () => {
    setSaving(true); setErr(""); setSaved(false);
    try {
      const r = await api.put("/admin/content/site-settings", s);
      setS(r.data);
      clearSiteSettingsCache();
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setSaving(false); }
  };

  if (!s) return null;
  const F = (k, label, placeholder) => (
    <div style={{ marginBottom: 12 }}>
      <label className="label">{label}</label>
      <input className="input" data-testid={`settings-${k}`} value={s[k] || ""}
             placeholder={placeholder}
             onChange={e => setS({ ...s, [k]: e.target.value })} />
    </div>
  );

  return (
    <div style={{ maxWidth: 640 }}>
      {F("company_name", "Company name", "CanadaMRO Exchange")}
      {F("office_address", "Office address", "Toronto, Ontario, Canada")}
      {F("support_email", "Support email", "support@canadamroexchange.ca")}
      {F("phone", "Phone", "+1-416-555-0000")}
      {F("copyright", "Footer copyright", "© 2026 CanadaMRO Exchange")}
      <div style={{ borderTop: "1px dashed var(--line)", margin: "16px 0 12px", paddingTop: 12 }}>
        <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 8 }}>SOCIAL LINKS</div>
      </div>
      {F("linkedin_url", "LinkedIn URL", "https://linkedin.com/company/…")}
      {F("facebook_url", "Facebook URL", "https://facebook.com/…")}
      {F("instagram_url", "Instagram URL", "https://instagram.com/…")}
      {F("twitter_url", "X (Twitter) URL", "https://x.com/…")}
      {F("youtube_url", "YouTube URL", "https://youtube.com/@…")}
      <div style={{ borderTop: "1px dashed var(--line)", margin: "16px 0 12px", paddingTop: 12 }}>
        <div className="font-mono" style={{ fontSize: 10, letterSpacing: ".15em", color: "var(--ink-mute)", marginBottom: 8 }}>LANDING PAGE</div>
      </div>
      {F("pricing_tagline", "Pricing tagline (homepage strip)", "Free during pilot · No commission on trades · SaaS-only when paid plans launch")}
      {err && <div style={{ color: "var(--red)" }} data-testid="settings-error">{err}</div>}
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <button className="btn btn-primary" data-testid="settings-save" onClick={save} disabled={saving}>
          <Save size={13} /> {saving ? "Saving…" : "Save"}
        </button>
        {saved && <span style={{ color: "var(--emerald)", fontSize: 13 }} data-testid="settings-saved">Saved ✓</span>}
      </div>
    </div>
  );
}
